/**
 * UserTable Component
 *
 * Displays a table of users and pending invitations with actions (view, edit, delete).
 * Includes sorting, status badges, and admin indicators.
 */

import React from 'react';
import { UserCircleIcon, EnvelopeIcon, EyeIcon, PencilIcon, TrashIcon } from '@heroicons/react/24/outline';
import { Button } from '../../../components/ui/Button';
import { Badge } from '../../../components/ui/Badge';
import { TableSkeleton } from '../../../components/ui/TableSkeleton';
import { EmptyState } from '../../../components/ui/EmptyState';
import { UserStatusBadge, UserStatus } from './UserStatusBadge';
import type { UserSanitized, InvitationSanitized } from '../../../types/user.types';
import { formatRelativeTime } from '../../../lib/utils/format';

/**
 * Combined row type for users and invitations
 */
export type UserTableRow =
  | { type: 'user'; data: UserSanitized }
  | { type: 'invitation'; data: InvitationSanitized };

interface UserTableProps {
  rows: UserTableRow[];
  isLoading: boolean;
  onViewUser: (user: UserSanitized) => void;
  onEditUser?: (user: UserSanitized) => void;
  onDeleteUser: (user: UserSanitized) => void;
  onCancelInvitation: (invitation: InvitationSanitized) => void;
  /** Permission to edit users */
  canEdit?: boolean;
  /** Permission to delete users */
  canDelete?: boolean;
}

export const UserTable: React.FC<UserTableProps> = ({
  rows,
  isLoading,
  onViewUser,
  onEditUser,
  onDeleteUser,
  onCancelInvitation,
  canEdit,
  canDelete,
}) => {
  if (isLoading) {
    return <TableSkeleton rows={5} columns={5} />;
  }

  if (rows.length === 0) {
    return (
      <EmptyState
        icon={<UserCircleIcon className="mx-auto h-12 w-12" />}
        title="No users found"
        description="Get started by inviting a new user to your tenant."
      />
    );
  }

  return (
    <div className="overflow-x-auto">
      <table className="min-w-full divide-y divide-gray-200">
        <thead className="bg-gray-50">
          <tr>
            <th
              scope="col"
              className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
            >
              User
            </th>
            <th
              scope="col"
              className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
            >
              Status
            </th>
            <th
              scope="col"
              className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
            >
              Assignments
            </th>
            <th
              scope="col"
              className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
            >
              Created
            </th>
            <th
              scope="col"
              className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider"
            >
              Actions
            </th>
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-200">
          {rows.map((row) => {
            if (row.type === 'user') {
              const user = row.data;
              const fullName = `${user.firstName} ${user.lastName}`;
              const initials = `${user.firstName[0]}${user.lastName[0]}`.toUpperCase();

              return (
                <tr key={user.userId} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <div className="flex-shrink-0 h-10 w-10">
                        <div className="h-10 w-10 rounded-full bg-primary-600 flex items-center justify-center text-white font-medium text-sm">
                          {initials}
                        </div>
                      </div>
                      <div className="ml-4">
                        <div className="text-sm font-medium text-gray-900">
                          {fullName}
                        </div>
                        <div className="text-sm text-gray-500">{user.email}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <UserStatusBadge status={user.status as UserStatus} />
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex gap-1 flex-wrap">
                      {user.roles && user.roles.length > 0 && (
                        <Badge variant="success">{user.roles.length} role{user.roles.length !== 1 ? 's' : ''}</Badge>
                      )}
                      {user.groups && user.groups.length > 0 && (
                        <Badge variant="warning">{user.groups.length} group{user.groups.length !== 1 ? 's' : ''}</Badge>
                      )}
                      {(!user.roles || user.roles.length === 0) && (!user.groups || user.groups.length === 0) && (
                        <span className="text-sm text-gray-400 italic">No assignments</span>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {formatRelativeTime(user.createdAt)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <div className="flex items-center justify-end gap-2">
                      <Button
                        variant="secondary"
                        size="sm"
                        onClick={() => onViewUser(user)}
                        title="View Details"
                      >
                        <EyeIcon className="h-4 w-4" />
                      </Button>
                      {canEdit && onEditUser && (
                        <Button
                          variant="secondary"
                          size="sm"
                          onClick={() => onEditUser(user)}
                          title="Edit User"
                        >
                          <PencilIcon className="h-4 w-4" />
                        </Button>
                      )}
                      {canDelete && (
                        <Button
                          variant="danger"
                          size="sm"
                          onClick={() => onDeleteUser(user)}
                          title="Delete User"
                        >
                          <TrashIcon className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                  </td>
                </tr>
              );
            } else {
              // Invitation row
              const invitation = row.data;
              const emailInitial = invitation.email[0].toUpperCase();

              return (
                <tr key={invitation.invitationId} className="hover:bg-gray-50 bg-amber-50/30">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <div className="flex-shrink-0 h-10 w-10">
                        <div className="h-10 w-10 rounded-full bg-amber-500 flex items-center justify-center text-white font-medium text-sm">
                          <EnvelopeIcon className="h-5 w-5" />
                        </div>
                      </div>
                      <div className="ml-4">
                        <div className="text-sm font-medium text-gray-900">
                          Pending Invitation
                        </div>
                        <div className="text-sm text-gray-500">{invitation.email}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <UserStatusBadge status="invited" />
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="text-sm text-gray-400">—</span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {formatRelativeTime(invitation.createdAt)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <div className="flex items-center justify-end gap-2">
                      {canDelete && (
                        <Button
                          variant="danger"
                          size="sm"
                          onClick={() => onCancelInvitation(invitation)}
                          title="Cancel Invitation"
                        >
                          <TrashIcon className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                  </td>
                </tr>
              );
            }
          })}
        </tbody>
      </table>
    </div>
  );
};
